import { useState } from "react";
import { toast } from "sonner";
import { Copy, Loader2, CheckCircle2, AlertTriangle, Clock, Info, ArrowRight, MinusCircle, Hourglass, ListChecks, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";

const ST = {
  none: ["Not Configured", "bg-slate-100 text-slate-600 border-slate-300", MinusCircle],
  pending: ["Pending", "bg-amber-50 text-amber-700 border-amber-300", Clock],
  verified: ["Forwarding Verified", "bg-emerald-50 text-emerald-700 border-emerald-300", CheckCircle2],
  failed: ["Failed", "bg-rose-50 text-rose-700 border-rose-300", AlertTriangle],
};

const RULE_SETTINGS = "Type: Permanent (301) · Setting: Forward only (no masking / no frame) · Destination starts with https://";

const GUIDES = (root) => [
  ["GoDaddy", [
    "Sign in at godaddy.com → My Products → next to your domain click DNS (or Manage DNS).",
    `Rule 1 (root domain): open the Forwarding tab → under "Domain" click Add Forwarding. Choose https://, paste the destination link, Forward type: Permanent (301), Settings: Forward only. Save.`,
    `Rule 2 (WWW subdomain): in the same Forwarding tab, under "Subdomain" click Add Forwarding. Type www as the subdomain (www.${root}), choose https://, paste the same destination link, Permanent (301), Forward only. Save.`,
    "If GoDaddy asks to change or replace DNS records, only allow it for @ and www. Never delete MX, TXT (SPF/DKIM) or other email records.",
    "You should now see two forwarding entries: the domain and www.",
  ]],
  ["Namecheap", [
    "Sign in at namecheap.com → Domain List → Manage next to your domain → Advanced DNS tab.",
    "Under Host Records click Add New Record → URL Redirect Record. Host: @, Value: paste the destination link, choose Permanent (301). Click the tick to save.",
    "Add a second URL Redirect Record: Host: www, Value: the same link, Permanent (301). Save.",
    "Do not choose Masked / Unmasked frame options. Leave Mail Settings and email records untouched.",
  ]],
  ["Hostinger", [
    "Sign in at hpanel.hostinger.com → Domains → your domain → Redirect (Domain Forwarding).",
    `Create a redirect for ${root}: paste the destination link, choose Permanent (301). Save.`,
    `Create a second redirect for www.${root} with the same link and Permanent (301). If Hostinger offers "include www", tick it instead and confirm both appear.`,
    "Don't change MX or other email records in DNS Zone Editor.",
  ]],
  ["Cloudflare", [
    "Sign in at dash.cloudflare.com and select your domain.",
    "DNS → Records: make sure both @ and www have a Proxied (orange cloud) record, e.g. A @ 192.0.2.1 and A www 192.0.2.1. Leave MX/TXT records as they are.",
    `Rules → Redirect Rules → Create rule. When: Hostname equals ${root} OR Hostname equals www.${root}. Then: Static redirect to the destination link, status 301. Deploy.`,
    "Cloudflare issues the SSL certificate automatically once the records are proxied.",
  ]],
  ["Other providers", [
    "Sign in where you bought the domain and look for Domain Forwarding, URL Redirect or Web Forwarding.",
    `Add a rule for ${root} → destination link, Permanent (301), Forward only (no masking/frame).`,
    `Add a second rule for www.${root} with the same settings, unless the provider says www is already included.`,
    "Use the https:// link, never http://. Don't delete or modify MX, SPF, DKIM or other unrelated records.",
  ]],
];

const ERRORS = [
  ["“doesn't open yet”", "Forwarding isn't active or the rule for that address is missing. Add the rule, wait at least 4 hours, then verify again."],
  ["“redirects to …”", "The destination is wrong (often http:// or a typo). Edit the rule and paste the exact https link shown above."],
  ["“uses masking”", "Change the forwarding type to Permanent (301), Forward only. Turn off masking / frame / stealth forwarding."],
  ["“opens a page instead of redirecting”", "The provider's parking page is still showing. Turn on forwarding for that exact address (root or www)."],
  ["Browser security / SSL warning", "The provider hasn't issued its certificate yet. Wait a few hours; if it persists, ask your provider to enable SSL/HTTPS for forwarding."],
];

const hostsOf = (domain) => {
  const d = (domain || "").toLowerCase();
  const root = d.startsWith("www.") ? d.slice(4) : d;
  return { root, hosts: [root, `www.${root}`] };
};

export function ForwardStatus({ status }) {
  const [l, cls, Icon] = ST[status] || ST.none;
  return <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-semibold ${cls}`} data-testid="forward-status-badge"><Icon className="h-3.5 w-3.5" />{l}</span>;
}

function Steps({ items, testid }) {
  return <ol className="mt-2 space-y-1.5" data-testid={testid}>{items.map((s, i) => <li key={i} className="flex gap-2 text-xs"><span className="h-5 w-5 shrink-0 rounded-full bg-emerald-600 text-white grid place-items-center text-[10px] font-bold">{i + 1}</span><span>{s}</span></li>)}</ol>;
}

function Guides({ root }) {
  const [open, setOpen] = useState(null);
  const g = GUIDES(root);
  return (
    <div className="space-y-1.5" data-testid="forward-guides">
      <div className="text-xs font-semibold">Step-by-step for your provider</div>
      <div className="flex flex-wrap gap-2">{g.map(([n], i) => <button key={n} type="button" onClick={() => setOpen(open === i ? null : i)} className={`text-xs px-3 py-1.5 rounded-full border ${open === i ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-700 hover:bg-slate-800"}`} data-testid={`forward-guide-${i}`}>{n}</button>)}</div>
      {open !== null && <Steps items={g[open][1]} testid="forward-guide-steps" />}
    </div>
  );
}

function Rules({ hosts, dest }) {
  return (
    <div className="rounded-lg border border-slate-800 p-3 space-y-2" data-testid="forward-rules">
      <div className="text-xs font-semibold">Add two forwarding rules (one for each address)</div>
      <p className="text-[11px] text-slate-500">Most providers treat the root domain and the www subdomain separately. Set up both, unless your provider clearly shows both are already covered by one rule.</p>
      <div className="grid gap-2 sm:grid-cols-2">{hosts.map((h, i) => (
        <div key={h} className="rounded-md border border-slate-700 p-2.5 text-xs space-y-1 min-w-0" data-testid={`forward-rule-${i}`}>
          <div className="text-[10px] uppercase text-slate-500">Rule {i + 1} · {i ? "WWW subdomain" : "Root domain"}</div>
          <div className="font-mono font-semibold break-all">{h}</div>
          <div className="flex items-start gap-1"><ArrowRight className="h-3.5 w-3.5 mt-0.5 shrink-0 text-emerald-600" /><span className="font-mono break-all">{dest}</span></div>
        </div>))}</div>
      <div className="text-[11px] text-slate-500" data-testid="forward-rule-settings">{RULE_SETTINGS}</div>
      <div className="text-[11px] flex gap-1.5 text-amber-700"><ShieldAlert className="h-3.5 w-3.5 shrink-0" />Do not use http://. Do not delete or modify MX, email, SPF, DKIM or other unrelated DNS records.</div>
    </div>
  );
}

function WaitAndTest({ hosts, dest }) {
  return (
    <div className="grid gap-3 md:grid-cols-2 items-start">
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs flex gap-2" style={{ color: "#92400E" }} data-testid="forward-wait-notice">
        <Hourglass className="h-4 w-4 shrink-0" /><span>Your domain provider may take some time to activate forwarding and issue its SSL certificate. SSL activation can take a few hours. We recommend waiting at least 4 hours before testing. In some cases, DNS or forwarding propagation may take up to 48 hours.</span>
      </div>
      <div className="rounded-lg border border-slate-800 p-3 text-xs space-y-1.5" data-testid="forward-checklist">
        <div className="font-semibold flex items-center gap-1.5"><ListChecks className="h-4 w-4" />After waiting, test it yourself</div>
        <ul className="space-y-1 list-disc ml-4">
          {hosts.map((h) => <li key={h}>Open <span className="font-mono break-all">https://{h}</span></li>)}
          <li>Both should redirect to <span className="font-mono break-all">{dest}</span></li>
          <li>Your website must load successfully.</li>
          <li>No SSL or browser security warning should appear.</li>
        </ul>
      </div>
    </div>
  );
}

function HostResults({ f, hosts }) {
  const res = Object.fromEntries((f.results || []).map((r) => [r.host || r.url?.replace(/^https?:\/\//, ""), r.ok]));
  if (!f.last_checked) return null;
  return (
    <div className="grid gap-2 sm:grid-cols-2" data-testid="forward-host-results">{hosts.map((h, i) => {
      const ok = res[h];
      return <div key={h} className={`rounded-md border p-2.5 text-xs flex items-center gap-2 min-w-0 ${ok ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-rose-300 bg-rose-50 text-rose-800"}`} data-testid={`forward-host-result-${i}`}>
        {ok ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <AlertTriangle className="h-4 w-4 shrink-0" />}
        <span className="font-mono break-all">{h}</span><span className="ml-auto font-semibold shrink-0">{ok ? "Verified" : "Needs attention"}</span></div>;
    })}</div>
  );
}

function ErrorHelp() {
  return (
    <details className="rounded-lg border border-slate-800 p-3 text-xs" data-testid="forward-error-help">
      <summary className="font-semibold cursor-pointer">Common errors and how to fix them</summary>
      <ul className="mt-2 space-y-1.5">{ERRORS.map(([e, fix]) => <li key={e}><b>{e}</b> — {fix}</li>)}</ul>
    </details>
  );
}

export default function DomainForward({ v, setV, publishFirst }) {
  const f = v.forwarding || null;
  const [dom, setDom] = useState(f?.domain || "");
  const [busy, setBusy] = useState(null);
  const run = async (k, fn) => { setBusy(k); try { setV((await fn()).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); } };
  const save = () => run("save", async () => { await publishFirst(false); return api.post(`/diy-sites/${v.project_id}/forwarding`, { domain: dom }); });
  const verify = () => run("verify", () => api.post(`/diy-sites/${v.project_id}/forwarding/verify`));
  const dest = f?.target || v.public_url;
  const fallback = hostsOf(f?.domain);
  const hosts = f?.results?.length ? f.results.map((r) => r.host || r.url.replace(/^https?:\/\//, "")) : fallback.hosts;
  const root = fallback.root;
  return (
    <div className="rounded-xl border border-slate-800 p-4 space-y-4" data-testid="domain-forward">
      <div className="flex items-start justify-between gap-2">
        <div><div className="text-sm font-semibold">Forward My Domain <span className="text-[10px] ml-1 px-1.5 py-0.5 rounded bg-emerald-600 text-white">FREE</span></div>
          <p className="text-xs text-slate-500 mt-1">Send visitors who type your domain to your LaunchX360 website. You set it up at your domain provider; LaunchX360 never changes your DNS.</p></div>
        <ForwardStatus status={f?.status || "none"} />
      </div>
      <div className="rounded-lg border border-cyan-300 bg-cyan-50 p-3 text-xs flex gap-2" style={{ color: "#155E75" }} data-testid="forward-explainer">
        <Info className="h-4 w-4 shrink-0" /><span>Forwarding is a redirect, not custom-domain hosting. Visitors arrive at your LaunchX360 link, so the browser address bar shows the LaunchX360 address, not your domain. Your LaunchX360 link stays your permanent, secure (https) website address.</span>
      </div>
      <div className="flex flex-wrap gap-2">
        <Input className="w-full sm:w-72" placeholder="yourproject.com" value={dom} onChange={(e) => setDom(e.target.value)} data-testid="forward-domain-input" />
        <Button disabled={!!busy || !dom.trim()} onClick={save} data-testid="forward-save">{busy === "save" && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{f ? "Update domain" : "Continue"}</Button>
        {f && <Button variant="ghost" disabled={!!busy} onClick={() => run("rm", () => api.delete(`/diy-sites/${v.project_id}/forwarding`))} data-testid="forward-remove">Remove</Button>}
      </div>
      {f && <>
        <div className="rounded-lg border border-slate-800 p-3 space-y-1" data-testid="forward-destination">
          <div className="text-[10px] uppercase text-slate-500">Destination for every forwarding rule</div>
          <div className="flex items-center gap-2 flex-wrap"><ArrowRight className="h-4 w-4 text-emerald-600" /><span className="font-mono text-sm break-all" data-testid="forward-destination-url">{dest}</span>
            <button type="button" onClick={() => { navigator.clipboard?.writeText(dest); toast.success("Copied"); }} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-slate-700 hover:bg-slate-800" data-testid="forward-copy"><Copy className="h-3 w-3" />Copy</button></div>
        </div>
        <Rules hosts={hosts} dest={dest} />
        <Guides root={root} />
        <WaitAndTest hosts={hosts} dest={dest} />
        <div className="space-y-1">
          <Button onClick={verify} disabled={!!busy} className="w-full sm:w-auto" data-testid="forward-verify">{busy === "verify" ? <><Loader2 className="h-4 w-4 animate-spin mr-1" />Checking each address...</> : "Verify Forwarding"}</Button>
          <div className="text-[11px] text-slate-500">We check {hosts.join(" and ")} separately. Setup is complete only when every address passes.</div>
        </div>
        {f.last_checked && <div className="text-[11px] text-slate-500" data-testid="forward-last-checked">Last checked {new Date(f.last_checked).toLocaleString()}</div>}
        <HostResults f={f} hosts={hosts} />
        {f.status === "verified" && <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-xs" style={{ color: "#065F46" }} data-testid="forward-success">Forwarding verified for {hosts.join(" and ")}: visitors now reach your website at {dest}. The address bar will show the LaunchX360 link.</div>}
        {f.status === "pending" && f.last_checked && <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs" style={{ color: "#92400E" }} data-testid="forward-partial">One address works, the other still needs attention. Setup isn't complete until both pass.</div>}
        {f.problems?.length > 0 && <div className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-xs space-y-1" style={{ color: "#9F1239" }} data-testid="forward-problems">
          <div className="font-semibold">What to fix</div><ul className="list-disc ml-4 space-y-1">{f.problems.map((p, i) => <li key={i}>{p}</li>)}</ul></div>}
        <ErrorHelp />
      </>}
    </div>
  );
}
